/**
 * useRecordCancelFlowDowngrade
 *
 * Returns a function to call AFTER a successful downgrade mutation. If the
 * downgrade was initiated from the cancel-subscription dialog (the user
 * picked "It's too expensive" → "Switch to Free" / "Switch to Standard"),
 * writes a `cancelPlanChange` userSurveys row attributing the change to the
 * cancel flow, then clears the source so a later downgrade started from
 * elsewhere won't re-fire it. Fire-and-forget; survey errors are logged but
 * never block the calling flow.
 *
 * Pairs with `useSubscriptionWizard`'s in-house `recordCancelFlowPlanSwitch`
 * (same `cancelPlanChange` formType for the wizard plan-change path). The
 * cancel_reason question distinguishes switch_plans vs too_expensive.
 */
import { useCallback } from 'react';
import { useMutation } from '@apollo/client/react';
import { SUBMIT_USER_SURVEY, type SubmitUserSurveyVariables } from '@scio3/graphql-client';
import { useBillingDialogStore } from '../stores/billing-dialog-store';

export function useRecordCancelFlowDowngrade() {
  const [submitSurvey] = useMutation<unknown, SubmitUserSurveyVariables>(SUBMIT_USER_SURVEY);
  const cancelFlowSource = useBillingDialogStore((s) => s.cancelFlowSource);
  const setCancelFlowSource = useBillingDialogStore((s) => s.setCancelFlowSource);

  // useCallback so the returned function has stable identity across renders —
  // lets callers list it as a useCallback dep without churn.
  return useCallback(
    (params: { targetPlan: 'free' | 'standard' | 'proPlus'; viaTrial?: boolean }) => {
      if (cancelFlowSource !== 'too_expensive') return;
      setCancelFlowSource(null);
      const questions: SubmitUserSurveyVariables['input']['questions'] = [
        {
          key: 'cancel_reason',
          text: 'Why are you cancelling?',
          answer: 'too_expensive',
          answerLabel: "It's too expensive",
        },
        {
          key: 'downgraded_to',
          text: 'Downgraded to',
          answer: params.targetPlan,
          answerLabel: params.targetPlan,
        },
      ];
      if (params.viaTrial !== undefined) {
        questions.push({
          key: 'via_trial',
          text: 'Was the account on trial?',
          answer: String(params.viaTrial),
          answerLabel: params.viaTrial ? 'yes' : 'no',
        });
      }
      void submitSurvey({
        variables: {
          input: {
            // formId is the form (cancel-sub dialog); formType is the outcome.
            formId: 'cancelSubscription',
            formVersion: 1,
            formType: 'cancelPlanChange',
            questions,
          },
        },
      }).catch((err) => {
        console.error('submitUserSurvey (cancelPlanChange) failed', err);
      });
    },
    [cancelFlowSource, setCancelFlowSource, submitSurvey],
  );
}
